import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Send, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { sendMessage, validateMessage } from '../../utils/messageUtils';

const MessageTest = () => {
  const { user } = useAuth();
  const [testMessage, setTestMessage] = useState('¡Hola! Este es un mensaje de prueba desde FemSportNet 🏆');
  const [testRecipient, setTestRecipient] = useState('test-recipient-id');
  const [testResult, setTestResult] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const runMessageTest = async () => {
    if (!user) {
      setTestError('Usuario no autenticado');
      setTestResult('error');
      return;
    }

    setTestResult('testing');
    setTestError('');

    try {
      // Validate message
      const validation = validateMessage(testMessage, selectedFiles);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Send test message
      const message = await sendMessage(user.id, testRecipient, {
        content: testMessage,
        attachments: selectedFiles
      });

      console.log('Test message sent successfully:', message);
      setTestResult('success');
      
    } catch (error) {
      console.error('Message test failed:', error);
      setTestError(error instanceof Error ? error.message : 'Error desconocido');
      setTestResult('error');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(Array.from(files).slice(0, 3));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Prueba de Mensajería
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mensaje de Prueba
          </label>
          <textarea
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Escribe un mensaje de prueba..."
          />
          <p className="text-xs text-gray-500 mt-1">{testMessage.length}/2000 caracteres</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Archivos Adjuntos (Opcional)
          </label>
          <input
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {selectedFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {selectedFiles.map((file, index) => (
                <div key={index} className="text-sm text-gray-600 dark:text-gray-400">
                  📎 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={runMessageTest}
          disabled={testResult === 'testing' || !testMessage.trim()}
          className={`w-full flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors ${
            testResult === 'testing' || !testMessage.trim()
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
              : 'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          {testResult === 'testing' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              Enviando mensaje de prueba...
            </>
          ) : (
            <>
              <Send size={16} className="mr-2" />
              Probar Envío de Mensaje
            </>
          )}
        </button>

        {/* Test Results */}
        {testResult === 'success' && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                  ✅ Mensaje enviado exitosamente
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  La funcionalidad de mensajería está funcionando correctamente.
                  {selectedFiles.length > 0 && ` Se enviaron ${selectedFiles.length} archivos adjuntos.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {testResult === 'error' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                  ❌ Error en el envío de mensaje
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {testError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Información de la Prueba
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                <li>• Esta prueba verifica que los mensajes se puedan enviar correctamente</li>
                <li>• Se valida el contenido y los archivos adjuntos</li>
                <li>• En modo demo, se simula el envío sin usar la base de datos</li>
                <li>• Los archivos adjuntos se subirían a Supabase Storage en producción</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageTest;